import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Title2 } from '@sorare/core/src/atoms/typography';
import { ORIGIN } from '@sorare/core/src/config';
import useCurrentSport from '@sorare/core/src/hooks/useCurrentSport';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';

import { ENTER_FROM_ANDROID_MARKETPLACE_TAB_PARAM } from 'components/mobileApp/EnterFromAndroidMarketplaceTab';

import footballBg from './assets/football.jpg';
import mlbBg from './assets/mlb.jpg';
import nbaBg from './assets/nba.jpg';

const Wrapper = styled(Vertical).attrs({ center: true, gap: 2 })<{
  background: string;
}>`
  background: ${({ background }) => `url(${background})`};
  background-size: cover;
  background-position: center;
  height: calc(
    100vh - var(--current-stack-height) - var(--navbar-height-mobile)
  );
  padding: 0 10% 20% 10%;
  justify-content: flex-end;
  text-align: center;
  color: var(--c-neutral-600);
  font: var(--t-20);

  b,
  button {
    font-weight: var(--t-bold);
    color: var(--c-neutral-1000);
    text-decoration: underline;
  }
`;

const backgrounds = {
  [Sport.FOOTBALL]: footballBg,
  [Sport.BASEBALL]: mlbBg,
  [Sport.NBA]: nbaBg,
};

export const DisabledMarket = () => {
  const sport = useCurrentSport();
  const background = backgrounds[sport];
  const { postMessage, canOpenBrowser, appBuildVersionNumber } =
    useIsMobileApp();

  const location = useLocation();

  const link = (content: ReactNode[]) => {
    if (canOpenBrowser) {
      return (
        <ButtonBase
          onClick={() =>
            postMessage('openBrowser', {
              url: `${ORIGIN}${location.pathname}?${ENTER_FROM_ANDROID_MARKETPLACE_TAB_PARAM}=${appBuildVersionNumber}`,
            })
          }
        >
          {content}
        </ButtonBase>
      );
    }

    return <b>{content}</b>;
  };

  return (
    <Wrapper background={background}>
      <Title2 color="var(--c-neutral-1000)">
        <FormattedMessage
          id="DisabledMarket.title"
          defaultMessage="The Marketplace is not{br}available in the app"
          values={{
            br: <br />,
          }}
        />
      </Title2>
      <span>
        <FormattedMessage
          id="DisabledMarket.description"
          defaultMessage="To buy officially licensed cards please{br}visit <b>sorare.com</b>"
          values={{ b: link, br: <br /> }}
        />
      </span>
    </Wrapper>
  );
};
